<template>
  <iframe
    ref="frame"
    class="ae-html-card"
    :style="{ height: height + 'px' }"
    sandbox="allow-scripts"
    referrerpolicy="no-referrer"
    :srcdoc="document"
    title="Agent answer"
    @load="measure"
  />
</template>

<script>
import axios from 'axios';

// The web half of the iPad app's HTML answer card
// (kairon-agent/KaironAgent/Views/HTMLAnswerCard.swift). Same contract: the
// agent writes one self-contained document, the renderer injects the head —
// charset, viewport, the CSP and the design tokens — and the frame reports its
// own height so the card grows with <details> toggles.
//
// sandbox="allow-scripts" is the ONLY capability granted: no forms, no
// navigation, no same-origin. The origin stays opaque, so the document cannot
// reach this app, its cookies or the network — and because it IS opaque, the
// host cannot read the document's height either (verified: contentDocument is
// null). That is why the height is posted out by an injected script rather
// than measured from here, the same arrangement as the iPad app's size
// handler.
// script-src covers the injected height reporter. The agent's own scripts are
// still inert in a static document: it is told not to write any, and nothing
// in it is given a way to reach the host.
const CSP_STATIC = "default-src 'none'; style-src 'unsafe-inline'; img-src data:; script-src 'unsafe-inline';";
// A live document runs its own inline script. The origin stays opaque and
// default-src stays 'none', so it still cannot fetch, navigate or reach the
// parent — the ONLY new capability is running JS, and its only way out is
// postMessage to this component.
const CSP_LIVE = "default-src 'none'; style-src 'unsafe-inline'; img-src data:; script-src 'unsafe-inline';";

const HEAD = `
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="Content-Security-Policy"
      content="__CSP__">
<style>
:root {
    --ink:#1A1A2E; --body:#3C4257; --muted:#6B7280; --faint:#8A8FA0;
    --paper:#FAF9F6; --card:#FFFFFF; --hairline:#F0EFEC;
    --neutral-rail:#DAD8D2; --neutral-panel:#F5F4F1;
    --accent:#EF6C3B; --link:#C25326;
    --risk:#C0392B; --risk-tint:#FBEAE6;
    --attention:#B08A3A; --attention-tint:#FBF1E1;
    --stable:#2F6B49; --stable-tint:#E9F3ED;
    --info:#3E5C8A; --info-tint:#EEF1F7; --snf:#7A5EA8;
}
body {
    margin: 16px;
    font: 15px/1.5 -apple-system, system-ui, sans-serif;
    color: var(--body);
    background: var(--card);
    -webkit-text-size-adjust: 100%;
}
</style>`;

// Injected by the host, never written by the agent — the same arrangement as
// the iPad app's size WKUserScript. kairon.rows(name, params) posts a request
// out and resolves when the host posts the rows back. The document never sees
// SQL, a source id, or a credential; it asks for a name its own sources list
// declared and the server resolves the rest.
// Injected into every document. A sandboxed frame has an opaque origin, so the
// host cannot read its height — the document must post it out.
const SIZER = `<script>
(function () {
  var post = function () {
    parent.postMessage({ kairon: 'height', value: document.documentElement.scrollHeight }, '*');
  };
  new ResizeObserver(post).observe(document.documentElement);
  post();
})();
<\/script>`;

const BRIDGE = `<script>
(function () {
  var next = 0, waiting = {};
  window.addEventListener('message', function (e) {
    var m = e.data || {};
    if (m.kairon !== 'rows' || !waiting[m.id]) return;
    var pending = waiting[m.id]; delete waiting[m.id];
    if (m.error) pending.reject(new Error(m.error)); else pending.resolve(m);
  });
  window.kairon = {
    rows: function (source, params) {
      var id = ++next;
      parent.postMessage({ kairon: 'rows', id: id, source: source, params: params || {} }, '*');
      return new Promise(function (resolve, reject) {
        waiting[id] = { resolve: resolve, reject: reject };
        setTimeout(function () {
          if (waiting[id]) { delete waiting[id]; reject(new Error('The data request timed out.')); }
        }, 30000);
      });
    }
  };
  var post = function () {
    parent.postMessage({ kairon: 'height', value: document.documentElement.scrollHeight }, '*');
  };
  new ResizeObserver(post).observe(document.documentElement);
  post();
})();
<\/script>`;

export default {
  name: 'AgentHtmlCard',
  props: {
    html: { type: String, required: true },
    // Sources the document may read, from the same agent turn:
    // [{ name, kind, id }]. Absent or empty means a static document, which
    // renders under the stricter no-scripts sandbox.
    sources: { type: Array, default: () => [] },
    sessionId: { type: String, default: '' },
    // Set when the card renders a SAVED page: rows then come from the page's
    // own endpoint, which re-validates the source against the stored list.
    pageSlug: { type: String, default: '' },
  },
  data() {
    return { height: 120 };
  },
  computed: {
    live() {
      return !!(this.sources && this.sources.length);
    },
    document() {
      const doc = this.html || '';
      const head0 = HEAD.replace('__CSP__', this.live ? CSP_LIVE : CSP_STATIC)
        + (this.live ? BRIDGE : SIZER);
      const lowered = doc.toLowerCase();
      const head = lowered.indexOf('<head>');
      if (head !== -1) {
        const at = head + '<head>'.length;
        return doc.slice(0, at) + head0 + doc.slice(at);
      }
      const htmlTag = lowered.indexOf('<html');
      if (htmlTag !== -1) {
        const close = doc.indexOf('>', htmlTag);
        if (close !== -1) {
          return `${doc.slice(0, close + 1)}<head>${head0}</head>${doc.slice(close + 1)}`;
        }
      }
      return `<!doctype html><html><head>${head0}</head><body>${doc}</body></html>`;
    },
  },
  watch: {
    document() {
      this.$nextTick(this.measure);
    },
  },
  mounted() {
    window.addEventListener('message', this.onMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onMessage);
  },
  methods: {
    onMessage(event) {
      const frame = this.$refs.frame;
      // Only this card's own frame; a sandboxed document has an opaque origin,
      // so identity is established by source, not by origin string.
      if (!frame || event.source !== frame.contentWindow) return;
      const msg = event.data || {};
      if (msg.kairon === 'height') {
        this.height = Math.max(60, Number(msg.value) || 60);
      } else if (msg.kairon === 'rows') {
        this.fetchRows(msg);
      }
    },
    async fetchRows(msg) {
      const frame = this.$refs.frame;
      const reply = (body) => frame && frame.contentWindow
        && frame.contentWindow.postMessage({ kairon: 'rows', id: msg.id, ...body }, '*');
      const source = (this.sources || []).find((s) => s.name === msg.source);
      if (!source) {
        reply({ error: `This card declares no source named '${msg.source}'.` });
        return;
      }
      try {
        const url = this.pageSlug
          ? `/api/impact_analytics/agent-pages/${this.pageSlug}/rows/`
          : `/api/impact_analytics/agent-sessions/${this.sessionId}/rows/`;
        const { data } = await axios.post(url, {
          source: source.name, kind: source.kind, id: source.id, params: msg.params || {},
        });
        reply({ columns: data.columns || [], rows: data.rows || [] });
      } catch (error) {
        reply({ error: error?.response?.data?.error || 'The data request failed.' });
      }
    },
    // The document cannot post its own height (no scripts), so read it.
    measure() {
      // The document posts its height; this only catches one that never does.
      setTimeout(() => { if (this.height <= 120) this.height = 420; }, 600);
    },
  },
};
</script>
